import { api } from '../config/api';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';

export interface DashboardStats {
  total_colis: number;
  colis_livres: number;
  colis_en_cours: number;
  colis_annules: number;
  revenus_total: number;
  revenus_mois: number;
  total_livreurs: number;
  livreurs_actifs: number;
  colis_par_jour: Array<{ date: string; count: number }>;
  colis_par_statut: Array<{ statut: string; count: number }>;
  top_livreurs: Array<{ nom: string; livraisons: number }>;
}

export interface RevenusParMois {
  annee: number;
  mois: Array<{
    mois: string;
    mois_key: string;
    montant: number;
    nb_factures: number;
  }>;
  total_annee: number;
  meilleur_mois: string;
}

export interface KpisAvances {
  delai_moyen_jours: number | null;
  taux_livraison: number;
  taux_annulation: number;
  top_villes: Array<{ ville: string; count: number }>;
  performance_livreurs: Array<{
    nom: string;
    livres: number;
    total: number;
    taux: number;
  }>;
  evolution_semaine: Array<{ semaine: string; count: number }>;
}

export type Periode = 'week' | 'month' | 'year';

export interface PeriodSnapshot {
  total_colis: number;
  colis_livres: number;
  taux_livraison: number;
  revenus: number;
}

export interface ComparaisonData {
  actuel: PeriodSnapshot;
  precedent: PeriodSnapshot;
  deltas: {
    total_colis: number;
    colis_livres: number;
    taux_livraison: number;
    revenus: number;
  };
  periode_actuelle: { debut: string; fin: string };
  periode_precedente: { debut: string; fin: string };
}

export interface FunnelStep {
  statut: string;
  count: number;
  pct: number;
  color: string;
}

export interface FunnelData {
  total: number;
  annule: number;
  steps: FunnelStep[];
}

export interface HeatmapCell {
  dow: number;
  hour: number;
  count: number;
}

export interface HeatmapData {
  cells: HeatmapCell[];
  max_count: number;
}

export const getDateRange = (periode: Periode) => {
  const now = new Date();
  switch (periode) {
    case 'week':
      return { debut: subDays(now, 7), fin: now };
    case 'month':
      return { debut: startOfMonth(now), fin: endOfMonth(now) };
    case 'year':
      return { debut: new Date(now.getFullYear(), 0, 1), fin: now };
  }
};

export const analyticsService = {
  getDashboard: async (periode: Periode): Promise<DashboardStats> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/dashboard', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
    });
    return res.data;
  },

  getRevenusParMois: async (annee?: number): Promise<RevenusParMois> => {
    const res = await api.get('/analytics/revenus-par-mois', {
      params: { annee: annee || new Date().getFullYear() },
    });
    return res.data;
  },

  getKpisAvances: async (periode: Periode): Promise<KpisAvances> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/kpis-avances', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
    });
    return res.data;
  },

  exportCsv: async (periode: Periode): Promise<void> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/export/csv', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_colis_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },

  /** Recherche globale Ctrl+K */
  searchGlobal: async (q: string): Promise<{
    colis: any[];
    factures: any[];
    livreurs: any[];
    total: number;
  }> => {
    const res = await api.get('/analytics/search/global', { params: { q } });
    return res.data;
  },

  /** Alertes dashboard : factures en retard + colis bloqués + rétention hub + manifestes */
  getAlerts: async (): Promise<{
    factures_retard: any[];
    factures_retard_count: number;
    colis_bloques: any[];
    colis_bloques_count: number;
    colis_retenus_hub: any[];
    colis_retenus_hub_count: number;
    manifestes_colis_manquants: any[];
    manifestes_colis_manquants_count: number;
    dossiers_surestaries: any[];
    dossiers_surestaries_count: number;
  }> => {
    const res = await api.get('/analytics/alerts');
    return res.data;
  },

  /** Comparaison période actuelle vs précédente */
  getComparaison: async (periode: Periode): Promise<ComparaisonData> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/comparaison', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
    });
    return res.data;
  },

  /** Entonnoir de livraison */
  getFunnel: async (periode: Periode): Promise<FunnelData> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/funnel', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
    });
    return res.data;
  },

  /** Heatmap activité par heure × jour de semaine */
  getHeatmap: async (periode: Periode): Promise<HeatmapData> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/heatmap', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
    });
    return res.data;
  },

  exportExcel: async (periode: Periode): Promise<void> => {
    const { debut, fin } = getDateRange(periode);
    const res = await api.get('/analytics/export/excel', {
      params: {
        date_debut: format(debut, 'yyyy-MM-dd'),
        date_fin: format(fin, 'yyyy-MM-dd'),
      },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(
      new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `rapport_analytics_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  },
};
